import { useState } from "react";
import { isDrawable, needsChart, plotPair, timeTicks, valueTicks, type Point } from "../lib/sparkline";
import { axisDate, dayKeyAt, longDate } from "../lib/weekLine";

/* W10d's chart — ONE series or TWO, to the dataviz rules relayed 2026-09-19.
 *
 * ⚠️ NO COLOUR IS NAMED HERE. Strokes are `currentColor`; the CSS class decides which token, and
 * only two are ever used — the accent for the measured series, `--ht-text-secondary` for the
 * estimated-1RM line. Both are already validated in both themes, which is what makes dark mode
 * SELECTED rather than flipped. A hex in a chart is a hex that is right in one theme.
 *
 * ⚠️ TEXT WEARS TEXT TOKENS, NEVER THE SERIES COLOUR. Values, axis labels and the legend are
 * primary/secondary/muted ink; the coloured MARK beside a legend entry carries the identity. A
 * coloured number is a number a coach reads as a category.
 *
 * ⚠️ AND IDENTITY IS NEVER COLOUR ALONE: the measured series is a solid line with round markers,
 * the estimate is a DASHED line with no markers. Someone who cannot separate the two hues still
 * has two different objects.
 */

/* 🔴 THE FRAME GREW A GUTTER ON TWO SIDES, because a chart with no numbers on it is a shape.
   `pad` was 12 all round, which is enough to keep a marker off the edge and nowhere near enough
   to print a value beside it or a date under it — so the morning's verdict ("no axes, no dates")
   was a true reading of a frame that had nowhere to put them. The plot area is now inset from a
   LEFT gutter (values) and a BOTTOM gutter (dates); the geometry helper is given the inner box
   and knows nothing about either. */
const GUTTER = { left: 34, bottom: 16 };
const BOX = { width: 320 - GUTTER.left, height: 110 - GUTTER.bottom, pad: 12 };
const FRAME = { width: 320, height: 110 };

export type Series = { points: Point[]; label: string };

export function MetricChart({ series, estimate, unit, caveat, formatValue }: {
  series: Series;
  /** The estimated-1RM line. Absent for holds, bodyweight and body fat. */
  estimate?: Series;
  unit: string;
  caveat?: string | null;
  formatValue?: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const fmt = formatValue ?? ((v: number) => `${Math.round(v * 10) / 10}`);

  /* 🔴 ONE CALL, NOT TWO — AND THIS IS THE DEFECT OF 2026-09-19 (`lib/sparkline.ts`, `plotPair`).
     `plot` scales a series to fill the box using its OWN min and max, so calling it twice drew a
     60–70 kg weight series and an 80–95 kg estimate at the SAME height: the dashed line sat under
     the solid one and the distance between them — the only thing a second series is for — meant
     nothing. `BodyMetricsPanel`'s own header forbids exactly this ("TWO CHARTS, NEVER ONE WITH
     TWO AXES"); the sin was hidden inside a helper called twice. */
  const pair = plotPair(series.points,
    estimate && estimate.points.length > 0 ? estimate.points : null, BOX);
  const main = pair.main;
  const second = pair.second;

  /* 🔴 ONE READING IS A STAT TILE, NOT A CHART. A dot in an empty frame draws a shape around a
     number with no trend and invites a coach to read a direction nobody measured. The same
     threshold `oneAnswer` uses, so the card and its chart cannot disagree about whether there is
     a trend to see. */
  if (!needsChart(main.dots.length)) {
    const only = main.dots[0];
    return (
      <div className="metric-tile">
        <p className="ht-caption metric-tile-label">{series.label}</p>
        {only === undefined ? (
          <p className="ht-caption metric-tile-empty">Nothing recorded in this window.</p>
        ) : (
          <>
            <p className="metric-tile-value">{fmt(only.point.value)} <span className="metric-tile-unit">{unit}</span></p>
            <p className="ht-caption metric-tile-when">
              {longDate(dayKeyAt(only.point.atMs))} — one reading, so there is no
              trend to draw yet.
            </p>
          </>
        )}
      </div>
    );
  }

  /* ⚠️ "NOTHING TO DRAW" AND "THE GEOMETRY BROKE" LOOK IDENTICAL ON SCREEN — the browser draws
     neither and reports neither. Separated, so a defect cannot hide behind an empty window. */
  if (!isDrawable(main) || (second !== null && !isDrawable(second))) {
    return (
      <p className="ht-caption metric-chart-empty" role="alert">
        This chart could not be drawn — the series produced an impossible coordinate. The readings
        in the table below are unaffected.
      </p>
    );
  }

  const last = main.dots[main.dots.length - 1]!;
  const peak = main.dots.reduce((m, d) => (d.point.value > m.point.value ? d : m), main.dots[0]!);
  const active = hover === null ? null : main.dots[hover] ?? null;

  return (
    <figure className="metric-chart">
      <figcaption className="ht-caption metric-chart-title">{series.label}</figcaption>

      {/* THE LEGEND EXISTS ONLY WHEN THERE ARE TWO SERIES — a single series is named by the title
          above, and a legend for one thing is a row of furniture. */}
      {second !== null && estimate !== undefined && (
        <ul className="metric-chart-legend ht-caption">
          <li><span className="metric-chart-swatch is-measured" aria-hidden="true" />{series.label}</li>
          <li><span className="metric-chart-swatch is-estimate" aria-hidden="true" />{estimate.label}</li>
        </ul>
      )}

      <svg className="metric-chart-svg" viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
           width="100%" height={FRAME.height} aria-hidden="true" focusable="false"
           onMouseLeave={() => setHover(null)}>
        {/* The plot area is shifted right by the value gutter; everything inside `g` is in the
            geometry helper's own coordinates, so the helper never learns about the labels. */}
        <g transform={`translate(${GUTTER.left} 0)`}>
        {/* The baseline, recessive — a hint-coloured hairline, not a grid of them. */}
        <line className="metric-chart-axis" x1={BOX.pad} y1={BOX.height - BOX.pad}
              x2={BOX.width - BOX.pad} y2={BOX.height - BOX.pad} stroke="currentColor" strokeWidth="1" />

        {second !== null && second.path !== null && (
          <path className="metric-chart-estimate" d={second.path} fill="none" stroke="currentColor"
                strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" strokeLinejoin="round" />
        )}
        {main.path !== null && (
          <path className="metric-chart-line" d={main.path} fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        )}

        {/* ⚠️ MARKERS ARE 8 px ACROSS AND CARRY A SURFACE RING, so two that overlap stay two. */}
        {main.dots.map((d, i) => (
          <circle key={i} className="metric-chart-dot" cx={d.x} cy={d.y} r="4"
                  fill="currentColor" stroke="var(--ht-surface)" strokeWidth="2" />
        ))}

        {/* THE CROSSHAIR, and hit targets far larger than the marks — a 4 px dot is not a target. */}
        {active !== null && (
          <line className="metric-chart-crosshair" x1={active.x} y1={BOX.pad} x2={active.x}
                y2={BOX.height - BOX.pad} stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />
        )}
        {main.dots.map((d, i) => (
          <rect key={`hit-${i}`} x={d.x - 12} y={0} width={24} height={BOX.height}
                fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}

        {/* 🔴 W30(l) — THE DIRECT END LABELS ARE GONE, AND THE REASON THEY EXISTED IS KEPT HERE
            BECAUSE IT IS STILL TRUE OF A CHART WITH ROOM.
            They named each series at its own last point, on the dataviz rule that a legend makes
            the eye travel — it reads a name at the top, then has to find the line again, while a
            word at the end of the line is read where the line is.
            ⚠️ **WHAT KILLED THEM IS GEOMETRY, NOT THE RULE.** There is no right-hand gutter: the
            words were drawn INSIDE the plot, anchored `end` at `x = lastDot.x - 6`, so they sat
            ON the series they were naming. At the 330 px grid card this chart was drawn for,
            nobody noticed; at the 670 px detail column W30(d) gave it, `Weight` and `Est. 1RM`
            land across both lines' right-hand end, MEASURED in this session's own stills and in
            the BEFORE set's `Face Pull` card — it was never a regression, only invisible.
            ⚠️ **AND THE CONDITION IS WHY DROPPING THEM COSTS NOTHING.** They were drawn under
            `second !== null && estimate !== undefined` — the legend's condition, exactly — so the
            two series were always named twice on the same chart. The legend is the copy that
            cannot land on a line. ⚠️ The alternative (a right gutter wide enough for the longer
            word) narrows the plot on BOTH halves to solve a collision on one, and a label outside
            a 320-unit frame would be clipped by the viewBox rather than drawn. */}
        </g>

        {/* 🔴 THE VALUE AXIS — three numbers, and the chart had none until 2026-09-19. Bottom,
            middle, top says what the chart is OF and how big the change is; a tick every 20 px on
            a 94 px plot would be a ladder of numbers taller than the shape it annotates. They sit
            in the LEFT gutter, outside the plot area, so no label can land on the line. */}
        {valueTicks(pair.min, pair.max).map((v, i) => {
          const y = BOX.pad + (1 - (v - pair.min) / (pair.max - pair.min)) * (BOX.height - BOX.pad * 2);
          return (
            <text key={`v-${i}`} className="metric-chart-axis-label" x={GUTTER.left - 6} y={y + 3}
                  textAnchor="end">{fmt(v)}</text>
          );
        })}

        {/* 🔴 THE TIME AXIS — the data's OWN first and last day, never a round calendar date: a
            tick reading "Sep 1" on a series starting on the 4th invites a coach to read a gap
            that is only the label's. Nothing between them; the crosshair answers any question
            about a point inside the window. */}
        {timeTicks(pair.fromMs, pair.toMs).map((t, i) => (
          <text key={`t-${i}`} className="metric-chart-axis-label"
                x={t.anchor === "start" ? GUTTER.left + BOX.pad : GUTTER.left + BOX.width - BOX.pad}
                y={FRAME.height - 4} textAnchor={t.anchor === "start" ? "start" : "end"}>
            {axisDate(dayKeyAt(t.atMs))}
          </text>
        ))}
      </svg>

      {/* ⚠️ SELECTIVE DIRECT LABELS — the LAST value and the peak, never a number on every point.
          A value on every marker is the anti-pattern: it turns a shape into a table that is
          harder to read than a table. */}
      <p className="ht-caption metric-chart-marks">
        <span className="metric-chart-mark-last">Latest {fmt(last.point.value)} {unit}</span>
        {/* ⚠️ COMPARED BY VALUE, NOT BY IDENTITY. `reduce` keeps the FIRST maximum on a tie, so on
            a flat series the peak is a different object with the same number — and the line read
            "Latest 15 % · peak 15 %" on his real body-fat data. A label that repeats the number
            beside it is the "a value on every point" anti-pattern in miniature. */}
        {peak.point.value !== last.point.value && (
          <span className="metric-chart-mark-peak"> · peak {fmt(peak.point.value)} {unit}</span>
        )}
      </p>

      {/* The tooltip is text in TEXT ink, with the coloured mark beside it carrying identity. */}
      {active !== null && (
        <p className="ht-caption metric-chart-tip" role="status">
          <span className="metric-chart-swatch is-measured" aria-hidden="true" />
          {longDate(dayKeyAt(active.point.atMs))} — {fmt(active.point.value)} {unit}
        </p>
      )}

      {caveat != null && caveat !== "" && (
        <p className="ht-caption metric-chart-caveat">{caveat}</p>
      )}

      {/* The series, for anyone who cannot enter the picture. A table view exists as well; this is
          the chart's own reading, not a substitute for it. */}
      <span className="ht-visually-hidden">
        {series.label}, {main.dots.length} readings:{" "}
        {main.dots.map((d) => `${longDate(dayKeyAt(d.point.atMs))} ${fmt(d.point.value)} ${unit}`).join("; ")}
      </span>
    </figure>
  );
}
